import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { moduleAccent } from '@/styles/tokens';
import { resolveModuleFromPath } from '../moduleNav';
import { pageLabel } from '../pageLabel';
import type { PresencePeer, PresenceRoster } from './presence.api';

/**
 * Presence capsule — who else is in Amassa right now, and where.
 *
 * THE FACE WEARS ITS THREAD. Every face is ringed and tinted in the accent of
 * the module its person is working in, the same colour that module's rail
 * and breadcrumb carry. So the capsule reads at a glance ("three on CS/OPS,
 * one in Contracting") before anyone opens it. Hub home and module-less pages
 * take the brand.
 *
 * Here vs away: here = a tab in view with recent input. Away = Amassa open in
 * a background tab, or idle for 10 minutes. Away faces drop their thread and
 * go grey — they are not on any thread right now.
 *
 * Motion: none at rest. The live dot is STATIC (a perpetual pulse reads as an
 * AI tell — owner, 2026-08-12). The only movement is a one-shot pop when a
 * NEW face arrives, and reduced-motion removes even that.
 */

const JOIN_MS = 700;

export function PresenceCapsule({
  roster,
  compact = false,
}: {
  roster: PresenceRoster | null;
  /** Narrow top bar: one face + the count. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const { self, here, away } = useMemo(() => {
    const peers = roster?.peers ?? [];
    const others = peers.filter((p) => p.id !== roster?.selfId);
    return {
      self: peers.find((p) => p.id === roster?.selfId) ?? null,
      here: others.filter((p) => p.status === 'here'),
      away: others.filter((p) => p.status === 'away'),
    };
  }, [roster]);
  const others = useMemo(() => [...here, ...away], [here, away]);
  const joining = useJoiners(others);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // The room emptied while the roster was open.
  useEffect(() => {
    if (others.length === 0) setOpen(false);
  }, [others.length]);

  // Nobody else is in: the capsule is a living thing, so it is simply absent.
  if (others.length === 0) return null;

  const shown = others.slice(0, compact ? 1 : 4);
  const overflow = others.length - shown.length;
  const label =
    `${here.length} ${here.length === 1 ? 'colleague' : 'colleagues'} here now` +
    (away.length ? `, ${away.length} away` : '');

  return (
    <div ref={wrapRef} className="presence">
      <button
        ref={btnRef}
        type="button"
        className="presence__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}. Show who is in Amassa`}
        title={label}
      >
        <span className="presence__stack" aria-hidden>
          {shown.map((p) => (
            <Face key={p.id} peer={p} joining={joining.has(p.id)} />
          ))}
          {overflow > 0 && <span className="presence__face presence__face--more">+{overflow}</span>}
        </span>
        <span className="presence__count" aria-hidden>
          <span className={`presence__dot${here.length ? '' : ' is-empty'}`} />
          {here.length}
        </span>
      </button>

      {open && (
        <div className="presence__pop motion-scale" role="dialog" aria-label="Who is in Amassa">
          <span className="presence__edge" aria-hidden />
          <div className="presence__scroll">
            {here.length > 0 && (
              <Section title="Here now" count={here.length} live>
                {here.map((p) => (
                  <Row key={p.id} peer={p} />
                ))}
              </Section>
            )}
            {away.length > 0 && (
              <Section title="Away" count={away.length}>
                {away.map((p) => (
                  <Row key={p.id} peer={p} meta={awayFor(p.lastActiveAt)} />
                ))}
              </Section>
            )}
          </div>
          {self && (
            <div className="presence__self">
              <Row peer={self} you />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  live = false,
  children,
}: {
  title: string;
  count: number;
  live?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="presence__section">
      <h3 className="presence__kicker">
        {title} <span className={live ? 'presence__kicker-n is-live' : 'presence__kicker-n'}>{count}</span>
      </h3>
      {children}
    </section>
  );
}

function Row({ peer, meta, you = false }: { peer: PresencePeer; meta?: string; you?: boolean }) {
  const where = whereOf(peer.path);
  return (
    <div className="presence__row">
      <Face peer={peer} large />
      <span className="presence__who">
        <span className="presence__name">
          {peer.name}
          {you && <span className="presence__you"> · you</span>}
        </span>
        <span className="presence__where">
          {where.module && (
            <span
              className="presence__where-mod"
              style={{ color: peer.status === 'here' ? where.accent : undefined }}
            >
              {where.module}
            </span>
          )}
          {where.module && where.page && <span aria-hidden> › </span>}
          {where.page && <span>{where.page}</span>}
        </span>
      </span>
      {meta && <span className="presence__meta">{meta}</span>}
    </div>
  );
}

function Face({
  peer,
  joining = false,
  large = false,
}: {
  peer: PresencePeer;
  joining?: boolean;
  large?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const accent = whereOf(peer.path).accent;
  const cls =
    'presence__face' +
    (large ? ' presence__face--lg' : '') +
    (peer.status === 'away' ? ' is-away' : '') +
    (joining ? ' is-joining' : '');
  return (
    <span className={cls} style={{ '--thread': accent } as CSSProperties}>
      {peer.photoUrl && !broken ? (
        <img src={peer.photoUrl} alt="" loading="lazy" onError={() => setBroken(true)} />
      ) : (
        initialsOf(peer.name)
      )}
    </span>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

/** The breadcrumb's own labels, so "where" reads exactly like the top bar. */
export function whereOf(path: string): { module?: string; page?: string; accent: string } {
  const mod = resolveModuleFromPath(path);
  const accent = mod ? moduleAccent[mod] : 'var(--brand)';
  if (path === '/' || path === '/dashboard') return { page: 'Hub home', accent };
  const { module, page } = pageLabel(path);
  if (!module && !page) return { page: 'Amassa', accent };
  return { module, page, accent };
}

export function initialsOf(name: string): string {
  const parts = name
    .replace(/[._@-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** "4 min", "1 h 20", "3 h". Counted from the last time any tab was in use. */
export function awayFor(iso: string, now = Date.now()): string {
  const mins = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (!Number.isFinite(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m >= 5 ? `${h} h ${m}` : `${h} h`;
}

/**
 * Ids that appeared since the previous roster. The people already present
 * when the page loads are seeded as seen, so only genuine arrivals pop.
 */
function useJoiners(peers: PresencePeer[]): Set<string> {
  const seenRef = useRef<Set<string> | null>(null);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [joining, setJoining] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const ids = peers.map((p) => p.id);
    if (seenRef.current === null) {
      if (ids.length) seenRef.current = new Set(ids);
      return;
    }
    const seen = seenRef.current;
    const fresh = ids.filter((id) => !seen.has(id));
    ids.forEach((id) => seen.add(id));
    if (fresh.length === 0) return;
    setJoining((prev) => new Set([...prev, ...fresh]));
    // Not cleared when the roster changes again: a second update inside the
    // window must not leave these faces stuck mid-pop.
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      setJoining((prev) => {
        const next = new Set(prev);
        fresh.forEach((id) => next.delete(id));
        return next;
      });
    }, JOIN_MS);
    timersRef.current.add(t);
  }, [peers]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  return joining;
}
